import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { projectCategoryOptions, type EcorpResourceConfig } from '@command-center/admin-ecorp';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  usePageHeader,
} from '@command-center/ui-system';
import { Check, ChevronDown, Grid2X2, List, Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import { createRecord, deleteRecord, listRecords, updateRecord, type PortalRecord } from './api';
import {
  display,
  EmptyState,
  ErrorNotice,
  formatDate,
  ProjectCategoryBadge,
  projectCategories,
  Skeletons,
  StatusPill,
  useToast,
} from './lib';

type ViewMode = 'cards' | 'list';

export function ResourceView({
  resource,
  onContext,
  onOpenRecord,
  initialFilter,
  onCreate,
  createLabel,
  headerTitle,
  headerSubtitle,
}: {
  resource: EcorpResourceConfig;
  onContext(value: Record<string, unknown>): void;
  onOpenRecord?(item: PortalRecord): void;
  initialFilter?: { field: string; value: string };
  onCreate?: () => void;
  createLabel?: string;
  headerTitle?: ReactNode;
  headerSubtitle?: ReactNode;
}) {
  const [items, setItems] = useState<PortalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [search, setSearch] = useState('');
  const [filterField, setFilterField] = useState(
    initialFilter?.field ?? resource.filterFields[0] ?? '',
  );
  const [filterValue, setFilterValue] = useState(initialFilter?.value ?? '');
  const [projectCategoryFilters, setProjectCategoryFilters] = useState<string[]>(() =>
    initialFilter?.field === 'Project Category' && initialFilter.value ? [initialFilter.value] : [],
  );
  const [sortField, setSortField] = useState(resource.defaultSort?.field ?? resource.primaryField);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    resource.defaultSort?.direction ?? 'asc',
  );
  const [mode, setMode] = useState<ViewMode>(
    () => (localStorage.getItem(`ecorp-view-${resource.key}`) as ViewMode | null) ?? 'cards',
  );
  const [offset, setOffset] = useState<string>();
  const [nextOffset, setNextOffset] = useState<string>();
  const [selected, setSelected] = useState<PortalRecord>();
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const openCreate = useCallback(() => {
    if (onCreate) onCreate();
    else setCreating(true);
  }, [onCreate]);
  usePageHeader(
    {
      title: headerTitle ?? resource.label,
      subtitle: headerSubtitle ?? resource.description,
      actions: [
        {
          id: `new-${resource.key}`,
          label: createLabel ?? `New ${resource.singularLabel}`,
          icon: <Plus className="h-4 w-4" aria-hidden />,
          variant: 'primary',
          onClick: openCreate,
        },
      ],
    },
    [
      resource.key,
      resource.label,
      resource.description,
      resource.singularLabel,
      headerTitle,
      headerSubtitle,
      createLabel,
      openCreate,
    ],
  );
  const queryKey = useMemo(
    () =>
      `${resource.key}|${search}|${filterField}|${filterValue}|${projectCategoryFilters.join(',')}|${sortField}|${sortDirection}|${offset ?? ''}`,
    [
      resource.key,
      search,
      filterField,
      filterValue,
      projectCategoryFilters,
      sortField,
      sortDirection,
      offset,
    ],
  );
  async function load() {
    setLoading(true);
    setError(undefined);
    const params = new URLSearchParams({ pageSize: '50', sortField, sortDirection });
    if (search.trim()) params.set('search', search.trim());
    if (resource.key === 'projects') {
      projectCategoryFilters.forEach((category) => params.append('projectCategory', category));
    } else if (filterField && filterValue.trim()) {
      params.set('filterField', filterField);
      params.set('filterValue', filterValue.trim());
    }
    if (offset) params.set('offset', offset);
    try {
      const page = await listRecords(resource.key, params);
      setItems(page.items);
      setNextOffset(page.offset);
      onContext({
        resource: resource.key,
        search,
        filterField,
        filterValue,
        projectCategories: projectCategoryFilters,
        recordCount: page.items.length,
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load records.');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 250);
    return () => window.clearTimeout(timer);
  }, [queryKey]);
  useEffect(() => {
    setOffset(undefined);
    setSelected(undefined);
  }, [resource.key]);
  function setView(next: ViewMode) {
    setMode(next);
    localStorage.setItem(`ecorp-view-${resource.key}`, next);
  }
  function open(item: PortalRecord) {
    if (onOpenRecord) onOpenRecord(item);
    else {
      setSelected(item);
      onContext({ resource: resource.key, record: item });
    }
  }
  return (
    <section className="space-y-4">
      <Card>
        <CardContent className="flex flex-wrap gap-2 p-3">
          <div className="relative min-w-64 flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder={`Search ${resource.label.toLowerCase()}…`}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(undefined);
              }}
            />
          </div>
          {resource.key === 'projects' ? (
            <ProjectCategoryFilter
              value={projectCategoryFilters}
              onChange={(value) => {
                setProjectCategoryFilters(value);
                setOffset(undefined);
              }}
            />
          ) : (
            <>
              <Select
                value={filterField}
                onValueChange={(value) => {
                  setFilterField(value);
                  setOffset(undefined);
                }}
              >
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {resource.filterFields.map((name) => (
                    <SelectItem value={name} key={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="w-48"
                placeholder="Filter value"
                value={filterValue}
                onChange={(e) => {
                  setFilterValue(e.target.value);
                  setOffset(undefined);
                }}
              />
            </>
          )}
          <Select value={sortField} onValueChange={(value) => setSortField(value)}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {resource.listFields.map((name) => (
                <SelectItem value={name} key={name}>
                  Sort: {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => setSortDirection((value) => (value === 'asc' ? 'desc' : 'asc'))}
          >
            {sortDirection === 'asc' ? 'A–Z' : 'Z–A'}
          </Button>
          <Button
            variant={mode === 'cards' ? 'default' : 'outline'}
            size="icon"
            onClick={() => setView('cards')}
            aria-label="Card view"
          >
            <Grid2X2 className="h-4 w-4" />
          </Button>
          <Button
            variant={mode === 'list' ? 'default' : 'outline'}
            size="icon"
            onClick={() => setView('list')}
            aria-label="List view"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => void load()} aria-label="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </CardContent>
      </Card>
      {error && <ErrorNotice message={error} onRetry={() => void load()} />}
      {loading ? (
        <Skeletons />
      ) : items.length === 0 ? (
        <EmptyState
          title={`No ${resource.label.toLowerCase()} match this view.`}
          hint={
            search || filterValue || projectCategoryFilters.length
              ? 'Try clearing the search or filters.'
              : undefined
          }
        />
      ) : mode === 'cards' ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((item) => (
            <RecordCard key={item.id} item={item} resource={resource} onClick={() => open(item)} />
          ))}
        </div>
      ) : (
        <RecordTable items={items} resource={resource} onSelect={open} />
      )}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>{items.length} records on this page</span>
        <div className="flex gap-2">
          <Button variant="outline" disabled={!offset} onClick={() => setOffset(undefined)}>
            First page
          </Button>
          <Button variant="outline" disabled={!nextOffset} onClick={() => setOffset(nextOffset)}>
            Next page
          </Button>
        </div>
      </div>
      <RecordDialog
        resource={resource}
        item={selected}
        open={Boolean(selected)}
        editing={editing}
        onEditing={setEditing}
        onClose={() => {
          setSelected(undefined);
          setEditing(false);
        }}
        onSaved={(item) => {
          setSelected(item);
          void load();
        }}
        onDeleted={() => {
          setSelected(undefined);
          setEditing(false);
          void load();
        }}
      />
      <RecordDialog
        resource={resource}
        open={creating}
        editing
        onEditing={() => undefined}
        onClose={() => setCreating(false)}
        onSaved={() => {
          setCreating(false);
          void load();
        }}
      />
    </section>
  );
}

function ProjectCategoryFilter({
  value,
  onChange,
}: {
  value: string[];
  onChange(value: string[]): void;
}) {
  const label =
    value.length === 0
      ? 'Project Category'
      : value.length <= 2
        ? value.map((category) => (category === 'Heath' ? 'Health' : category)).join(', ')
        : `${value.length} categories`;
  const toggle = (category: string) =>
    onChange(
      value.includes(category) ? value.filter((item) => item !== category) : [...value, category],
    );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-56 justify-between font-normal">
          <span className="truncate">{label}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        <div className="space-y-1">
          <p className="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Project Category
          </p>
          {projectCategoryOptions.map((category) => {
            const selected = value.includes(category);
            return (
              <button
                key={category}
                type="button"
                aria-pressed={selected}
                className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition hover:bg-muted"
                onClick={() => toggle(category)}
              >
                <span className="flex h-4 w-4 items-center justify-center rounded border">
                  {selected && <Check className="h-3 w-3 text-primary" />}
                </span>
                <ProjectCategoryBadge category={category} />
              </button>
            );
          })}
        </div>
        {value.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-2 w-full"
            onClick={() => onChange([])}
          >
            Clear categories
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function documentNeedsApproval(item: PortalRecord): boolean {
  return (
    display(item.fields['Extraction Status']) === 'Needs Review' ||
    display(item.fields['Processing Status']) === 'Needs Review' ||
    ['In Review', 'Changes Requested'].includes(display(item.fields['Review Status']))
  );
}
export function RecordCard({
  item,
  resource,
  onClick,
}: {
  item: PortalRecord;
  resource: EcorpResourceConfig;
  onClick(): void;
}) {
  if (resource.key === 'projects') {
    return <ProjectRecordCard item={item} resource={resource} onClick={onClick} />;
  }
  const approval = resource.key === 'documents' && documentNeedsApproval(item);
  return (
    <Card
      className="cursor-pointer transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
      onClick={onClick}
    >
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          {approval && (
            <span
              className="h-2 w-2 shrink-0 rounded-full bg-amber-500"
              title="Review or approval needed"
            />
          )}
          {display(item.fields[resource.primaryField]) || 'Untitled'}
          {approval && <Badge variant="secondary">Needs approval</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {resource.cardFields.map((name) => (
          <div key={name} className="flex justify-between gap-3 text-sm">
            <span className="text-muted-foreground">{name}</span>
            <span className="max-w-[60%] text-right">
              {typeof item.fields[name] === 'boolean' ? (
                <Badge variant={item.fields[name] ? 'default' : 'secondary'}>
                  {item.fields[name] ? 'Yes' : 'No'}
                </Badge>
              ) : isStatusField(name) ? (
                <StatusPill value={item.fields[name]} />
              ) : (
                display(item.fields[name]) || '—'
              )}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function ProjectRecordCard({
  item,
  resource,
  onClick,
}: {
  item: PortalRecord;
  resource: EcorpResourceConfig;
  onClick(): void;
}) {
  const categories = projectCategories(item.fields['Project Category']);
  const bodyFields = resource.cardFields.filter(
    (name) => !['Project Category', 'CreatedDate', 'Last Updated'].includes(name),
  );
  const created = formatDate(item.fields.CreatedDate ?? item.createdTime) || '—';
  const updated = formatDate(item.fields['Last Updated']) || '—';

  return (
    <Card
      className="flex h-full cursor-pointer flex-col transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
      onClick={onClick}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="min-w-0 flex-1 truncate text-base">
            {display(item.fields[resource.primaryField]) || 'Untitled'}
          </CardTitle>
          {categories.length > 0 && (
            <div className="flex max-w-[50%] flex-wrap justify-end gap-1">
              {categories.map((category) => (
                <ProjectCategoryBadge key={category} category={category} />
              ))}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col">
        <div className="space-y-2 pb-4">
          {bodyFields.map((name) => (
            <div key={name} className="flex justify-between gap-3 text-sm">
              <span className="text-muted-foreground">{name}</span>
              <span className="max-w-[60%] text-right">
                {isStatusField(name) ? (
                  <StatusPill value={item.fields[name]} />
                ) : (
                  display(item.fields[name]) || '—'
                )}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-auto grid grid-cols-2 gap-3 border-t pt-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Created date
            </p>
            <p className="mt-1 text-sm">{created}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Last updated
            </p>
            <p className="mt-1 text-sm">{updated}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
export function RecordTable({
  items,
  resource,
  onSelect,
}: {
  items: PortalRecord[];
  resource: EcorpResourceConfig;
  onSelect(item: PortalRecord): void;
}) {
  return (
    <div className="overflow-x-auto rounded-xl border bg-card">
      <table className="w-full text-sm">
        <thead className="bg-muted/60 text-left">
          <tr>
            {resource.listFields.map((name) => (
              <th key={name} className="whitespace-nowrap px-3 py-2 font-medium">
                {name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={item.id}
              className="cursor-pointer border-t hover:bg-muted/30"
              onClick={() => onSelect(item)}
            >
              {resource.listFields.map((name) => (
                <td key={name} className="max-w-64 truncate px-3 py-2">
                  {name === resource.primaryField &&
                  resource.key === 'documents' &&
                  documentNeedsApproval(item) ? (
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="h-2 w-2 shrink-0 rounded-full bg-amber-500"
                        title="Review or approval needed"
                      />
                      {display(item.fields[name]) || '—'}
                    </span>
                  ) : isStatusField(name) ? (
                    <StatusPill value={item.fields[name]} />
                  ) : (
                    display(item.fields[name]) || '—'
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function isStatusField(name: string): boolean {
  return [
    'Project Status',
    'Processing Status',
    'Extraction Status',
    'Review Status',
    'Status',
  ].includes(name);
}

export function RecordDialog({
  resource,
  item,
  open,
  editing,
  onEditing,
  onClose,
  onSaved,
  onDeleted,
}: {
  resource: EcorpResourceConfig;
  item?: PortalRecord;
  open: boolean;
  editing: boolean;
  onEditing(value: boolean): void;
  onClose(): void;
  onSaved(item: PortalRecord): void;
  onDeleted?(): void;
}) {
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const { toast } = useToast();
  useEffect(() => {
    setDraft(item?.fields ?? {});
    setError(undefined);
  }, [item, open]);
  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const result = item
        ? await updateRecord(resource.key, item.id, draft)
        : await createRecord(resource.key, draft);
      onSaved(result.item);
      onEditing(false);
      toast(item ? `${resource.singularLabel} updated.` : `${resource.singularLabel} created.`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    if (
      !item ||
      !window.confirm(
        `Delete ${display(item.fields[resource.primaryField]) || `this ${resource.singularLabel.toLowerCase()}`}? This cannot be undone.`,
      )
    )
      return;
    setBusy(true);
    setError(undefined);
    try {
      await deleteRecord(resource.key, item.id);
      toast(`${resource.singularLabel} deleted.`);
      onDeleted?.();
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Delete failed.');
    } finally {
      setBusy(false);
    }
  }
  const writable = resource.fields.filter((field) => !field.readonly);
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {item
              ? display(draft[resource.primaryField]) || resource.singularLabel
              : `New ${resource.singularLabel}`}
          </DialogTitle>
        </DialogHeader>
        {editing ? (
          <form onSubmit={save} className="space-y-5">
            {resource.detailSections.map((section) => {
              const sectionFields = section.fields.filter((name) =>
                writable.some((field) => field.id === name),
              );
              if (!sectionFields.length) return null;
              return (
                <section key={section.label}>
                  <h3 className="mb-3 border-b pb-2 font-medium">{section.label}</h3>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {sectionFields.map((name) => {
                      const definition = writable.find((field) => field.id === name)!;
                      return (
                        <FieldInput
                          key={name}
                          definition={definition}
                          value={draft[name]}
                          onChange={(value) =>
                            setDraft((current) => ({ ...current, [name]: value }))
                          }
                        />
                      );
                    })}
                  </div>
                </section>
              );
            })}
            {error && <p className="text-sm text-destructive">{error}</p>}
            <DialogFooter>
              {item && (
                <Button
                  type="button"
                  variant="destructive"
                  className="mr-auto"
                  disabled={busy}
                  onClick={() => void remove()}
                >
                  <Trash2 className="mr-1.5 h-4 w-4" />
                  Delete
                </Button>
              )}
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-5">
            {resource.detailSections.map((section) => (
              <section key={section.label}>
                <h3 className="mb-2 border-b pb-2 font-medium">{section.label}</h3>
                <dl className="grid gap-3 sm:grid-cols-2">
                  {section.fields.map((name) => (
                    <div key={name}>
                      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {name}
                      </dt>
                      <dd className="mt-1 break-words text-sm">
                        {isStatusField(name) ? (
                          <StatusPill value={draft[name]} />
                        ) : (
                          display(draft[name]) || '—'
                        )}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            ))}
            {error && <p className="text-sm text-destructive">{error}</p>}
            <DialogFooter>
              {item && (
                <Button
                  variant="destructive"
                  className="mr-auto"
                  disabled={busy}
                  onClick={() => void remove()}
                >
                  <Trash2 className="mr-1.5 h-4 w-4" />
                  Delete
                </Button>
              )}
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <Button onClick={() => onEditing(true)}>Edit</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function FieldInput({
  definition,
  value,
  onChange,
}: {
  definition: EcorpResourceConfig['fields'][number];
  value: unknown;
  onChange(value: unknown): void;
}) {
  const [customOption, setCustomOption] = useState('');
  if (definition.type === 'boolean')
    return (
      <label className="field-label flex-row items-center gap-2">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
        {definition.label}
      </label>
    );
  if (definition.type === 'select' && definition.options?.length)
    return (
      <label className="field-label">
        {definition.label}
        <select
          className="field-control"
          value={display(value)}
          onChange={(e) => onChange(e.target.value || undefined)}
        >
          <option value="">—</option>
          {definition.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    );
  if (definition.type === 'multi-select') {
    const selected = Array.isArray(value)
      ? value.map((item) => display(item)).filter(Boolean)
      : display(value)
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
    const options = [
      ...new Set([...(definition.options?.map((option) => option.value) ?? []), ...selected]),
    ];
    const toggle = (option: string) =>
      onChange(
        selected.includes(option)
          ? selected.filter((item) => item !== option)
          : [...selected, option],
      );
    return (
      <fieldset className="field-label sm:col-span-2">
        <legend>{definition.label}</legend>
        <div className="flex flex-wrap gap-2">
          {options.map((option) => (
            <button
              type="button"
              key={option}
              className={`rounded-full border px-2.5 py-1 text-xs ${selected.includes(option) ? 'border-primary bg-primary text-primary-foreground' : 'bg-background'}`}
              onClick={() => toggle(option)}
            >
              {option}
            </button>
          ))}
        </div>
        <div className="mt-2 flex gap-2">
          <Input
            value={customOption}
            placeholder="Add another option"
            onChange={(event) => setCustomOption(event.target.value)}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              const option = customOption.trim();
              if (option && !selected.includes(option)) onChange([...selected, option]);
              setCustomOption('');
            }}
          >
            Add
          </Button>
        </div>
      </fieldset>
    );
  }
  if (definition.type === 'markdown' || definition.type === 'json')
    return (
      <label className="field-label sm:col-span-2">
        {definition.label}
        <textarea
          className="field-control min-h-24"
          value={display(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
    );
  const inputType =
    definition.type === 'date'
      ? 'date'
      : definition.type === 'datetime'
        ? 'datetime-local'
        : definition.type === 'number' || definition.type === 'currency'
          ? 'number'
          : definition.type === 'email'
            ? 'email'
            : definition.type === 'url'
              ? 'url'
              : 'text';
  return (
    <label className="field-label">
      {definition.label}
      <input
        className="field-control"
        type={inputType}
        required={definition.required}
        value={display(value)}
        step={inputType === 'number' ? 'any' : undefined}
        onChange={(e) =>
          onChange(
            inputType === 'number'
              ? e.target.value === ''
                ? undefined
                : Number(e.target.value)
              : e.target.value,
          )
        }
      />
    </label>
  );
}
